/**
 * PlayerHost (spec 009): every preview in the editor plays through the
 * canonical player. `PlayerHost` mounts spec 012's `SceneRuntime` for one
 * scene; `StoryPlayerHost` mounts spec 020's `StoryRuntime` for the story
 * runner. Both draw into the player's stage box (`playerStage`: the same
 * scaling, letterboxing and DOM choice panel as the published page), feed
 * the runtime the in-memory scene and media fetched through
 * `apiClient.getMediaFileBlob`, and play sound through the editor's audio
 * port (`previewAudio`, spec 007).
 *
 * Nothing here interpolates, times dialogs or lays out choices: the runtime
 * draws every frame, and its clock is the playhead the host reports.
 *
 * `PlayerHost` pauses at the choice reveal (`pauseAtReveal`, default on):
 * the frame holds at the reveal time with the panel clickable and looping
 * sounds still playing; clicking an option reports it through `onChoice`.
 */

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react';
import type {
  ChoiceOption,
  FrameState,
  Orientation,
  PlaybackSettings,
  SceneCompleteEvent,
  SheetCut,
  StoryPackageManifest,
} from '../../../../Shared/src/@types';
import { SceneRuntime } from '../../../../Shared/src/runtime/SceneRuntime';
import { StoryRuntime, type StorySceneChangeEvent } from '../../../../Shared/src/runtime/StoryRuntime';
import type { PlayableSceneInput } from '../../../../Shared/src/runtime/playableScene';
import {
  PLAYER_STYLE,
  applyStageFit,
  buildChoicePanel,
  lockChoicePanel,
  stackChoicePanel,
  stageFit,
} from '../../../../Shared/src/runtime/playerStage';
import { computeRevealTime } from '../../../../Shared/src/utils/sceneTiming';
import { sceneImageIds } from '../../../../Shared/src/runtime/browserMedia';
import { useApiClient } from '../../api/client';
import { createPreviewAudioPort, type PreviewAudioPort } from '../../utils/previewAudio';
import {
  cachedSceneImages,
  loadPreviewImageUrl,
  loadSceneImagesForPreview,
  previewMediaUrl,
} from '../../utils/previewMedia';
import { registerProbe } from '../../utils/conformanceHooks';
import { sceneSpriteMediaIds, useMediaSheetStore } from '../../stores/mediaSheetStore';

/**
 * The scene with the spritesheet cuts of its pictures (spec 033 D-3): the
 * scene's own `sheets` win, the editor's loaded cuts fill the rest. The same
 * object back when no cut applies, so a still-only scene is never re-sent.
 */
export function withLoadedSheets(scene: PlayableSceneInput, loaded: Record<number, SheetCut>): PlayableSceneInput {
  const own = (scene as { sheets?: Record<number, SheetCut> }).sheets ?? {};
  const extra: Record<number, SheetCut> = {};
  for (const id of sceneSpriteMediaIds(scene as never)) if (!own[id] && loaded[id]) extra[id] = loaded[id];
  if (Object.keys(extra).length === 0) return scene;
  return { ...scene, sheets: { ...own, ...extra } } as PlayableSceneInput;
}

/** The host's controls (`ref`). Times are ms on the runtime's clock. */
export interface PlayerHostHandle {
  /** A new play from `fromMs` (default 0). */
  play(fromMs?: number): void;
  pause(): void;
  /** Continue from the paused time. */
  resume(): void;
  /** Stop playback and sound; the frame stays. */
  stop(): void;
  /** Show the frame at `ms` without playing (a running play continues from there). */
  seek(ms: number): void;
  selectChoice(choiceId: string): void;
  isPlaying(): boolean;
  /** The play length (spec 012's `resolveSceneLength`). */
  getLength(): number;
  getFrameState(): FrameState | null;
}

export interface PlayerHostProps {
  /** The scene as the editor holds it (unsaved edits included). */
  scene: PlayableSceneInput;
  orientation: Orientation;
  /** When it turns true (or is true on mount) the scene plays from `startMs`; false stops it. */
  playing?: boolean;
  /** Where a declarative play (`playing` turning true) starts, ms. Read at that moment; default 0. */
  startMs?: number;
  /** Hold the frame at the choice reveal with the panel clickable. Default true. */
  pauseAtReveal?: boolean;
  /** The runtime's playhead: every frame while playing, and after a seek. */
  onPlayhead?: (ms: number) => void;
  /** The choice panel appeared (at `revealMs`). */
  onReveal?: (revealMs: number) => void;
  /** An option was chosen (clicked, or the timer when not paused at the reveal). */
  onChoice?: (choice: ChoiceOption) => void;
  /** The scene completed (`auto`, `choice` or `timeout`). */
  onComplete?: (event: SceneCompleteEvent) => void;
  className?: string;
  'data-testid'?: string;
}

/** Wait this long for images before a play starts without them (they appear when decoded). */
const IMAGE_WAIT_MS = 1500;

type HostElement = HTMLDivElement & { smPlayer?: PlayerHostHandle };

export const PlayerHost = forwardRef<PlayerHostHandle, PlayerHostProps>(function PlayerHost(props, ref) {
  const { orientation, playing = false, className } = props;
  // Every caller gets the cuts (spec 033): load them for the scene's pictures
  // and play the scene with them, re-sending it when a cut arrives late.
  const loadedSheets = useMediaSheetStore((s) => s.sheets);
  const mediaKey = sceneSpriteMediaIds(props.scene as never).join(',');
  useEffect(() => {
    if (mediaKey) void useMediaSheetStore.getState().loadSheets(mediaKey.split(',').map(Number));
  }, [mediaKey]);
  const scene = useMemo(() => withLoadedSheets(props.scene, loadedSheets), [props.scene, loadedSheets]);
  const apiClient = useApiClient();
  const rootRef = useRef<HostElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const runtimeRef = useRef<SceneRuntime | null>(null);
  const audioRef = useRef<PreviewAudioPort | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const propsRef = useRef(props);
  propsRef.current = { ...props, scene };
  const orientationRef = useRef(orientation);
  orientationRef.current = orientation;
  /** Bumped by every play/stop: a play waiting for images is dropped when stale. */
  const playToken = useRef(0);

  const removePanel = useCallback(() => {
    panelRef.current?.remove();
    panelRef.current = null;
  }, []);

  const revealTime = useCallback((): number => {
    const rt = runtimeRef.current;
    if (!rt) return 0;
    const s = rt.getScene();
    return computeRevealTime(s.duration, s.choiceSettings?.showBeforeEnd, s.exitTransition);
  }, []);

  const report = useCallback((ms: number) => propsRef.current.onPlayhead?.(ms), []);

  const fit = useCallback(() => {
    const root = rootRef.current;
    const stage = stageRef.current;
    const canvas = canvasRef.current;
    if (!root || !stage || !canvas) return;
    const o = orientationRef.current;
    const before = { w: canvas.width, h: canvas.height };
    applyStageFit(stage, canvas, o, stageFit(o, root.clientWidth, root.clientHeight));
    // A resized buffer is blank: draw the same frame again
    if (before.w !== canvas.width || before.h !== canvas.height) runtimeRef.current?.setOrientation(o);
  }, []);

  /** Start a new play once the scene's images are decoded (or after a short wait). */
  const startPlay = useCallback((fromMs: number) => {
    const rt = runtimeRef.current;
    if (!rt) return;
    const token = ++playToken.current;
    removePanel();
    const { images, missing } = cachedSceneImages(propsRef.current.scene as never);
    if (missing.length === 0) {
      if (Object.keys(images).length > 0) rt.setImages(images);
      rt.play(fromMs);
      return;
    }
    let started = false;
    const go = () => {
      if (started || token !== playToken.current || runtimeRef.current !== rt) return;
      started = true;
      rt.play(fromMs);
    };
    const timer = setTimeout(go, IMAGE_WAIT_MS);
    void loadSceneImagesForPreview(apiClient, propsRef.current.scene as never).then((loaded) => {
      if (runtimeRef.current !== rt) return;
      rt.setImages(loaded);
      clearTimeout(timer);
      go();
    });
  }, [apiClient, removePanel]);

  const handle = useRef<PlayerHostHandle>({
    play: (fromMs = 0) => startPlay(fromMs),
    pause: () => runtimeRef.current?.pause(),
    resume: () => runtimeRef.current?.resume(),
    stop: () => {
      playToken.current++;
      runtimeRef.current?.stop();
      removePanel();
    },
    seek: (ms: number) => {
      const rt = runtimeRef.current;
      if (!rt) return;
      if (rt.isChoiceRevealed() && ms < revealTime()) {
        // Back before the reveal: a fresh play there, so the panel comes again
        const wasPlaying = rt.isPlaying();
        removePanel();
        rt.play(ms);
        if (!wasPlaying) rt.pause();
      } else {
        rt.seek(ms);
      }
      if (panelRef.current) stackChoicePanel(panelRef.current, rt.getDialogBoxes(), rt.getFrameState().stage.height);
      report(rt.getPlayheadPosition());
    },
    selectChoice: (choiceId: string) => runtimeRef.current?.selectChoice(choiceId),
    isPlaying: () => runtimeRef.current?.isPlaying() ?? false,
    getLength: () => runtimeRef.current?.getScene().duration ?? 0,
    getFrameState: () => runtimeRef.current?.getFrameState() ?? null,
  });
  useImperativeHandle(ref, () => handle.current, []);

  // One runtime per mount, on the host's canvas
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const audio = createPreviewAudioPort(apiClient);
    audioRef.current = audio;
    const initial = propsRef.current.scene;
    const runtime = new SceneRuntime({
      scene: initial,
      canvas,
      orientation: orientationRef.current,
      audio: audio.port,
      playbackSettings: (initial as { playbackSettings?: Partial<PlaybackSettings> }).playbackSettings,
      onPlayheadUpdate: (ms) => {
        // A selection or a timeout plays the scene's exit with the panel locked (spec 015)
        if (panelRef.current && runtime.isExiting()) lockChoicePanel(panelRef.current);
        if (panelRef.current) stackChoicePanel(panelRef.current, runtime.getDialogBoxes(), runtime.getFrameState().stage.height);
        report(ms);
      },
      onChoicesReveal: (choices, settings) => {
        removePanel();
        const stage = stageRef.current;
        if (stage) {
          const { panel } = buildChoicePanel(choices, settings, (id) => {
            runtime.selectChoice(id);
            if (panelRef.current && runtime.isExiting()) lockChoicePanel(panelRef.current);
          });
          panel.setAttribute('data-testid', 'preview-choice-panel');
          panelRef.current = panel;
          stage.appendChild(panel);
        }
        const p = propsRef.current;
        if (p.pauseAtReveal !== false) {
          // Hold the reveal frame; loops keep playing (spec 008)
          runtime.pause({ holdLoops: true });
          const reveal = revealTime();
          // After the runtime's tick has drawn: settle on the reveal frame exactly
          queueMicrotask(() => {
            if (runtimeRef.current !== runtime || runtime.isPlaying()) return;
            runtime.seek(reveal);
            report(reveal);
            if (panelRef.current) stackChoicePanel(panelRef.current, runtime.getDialogBoxes(), runtime.getFrameState().stage.height);
          });
          p.onReveal?.(reveal);
        } else {
          if (panelRef.current) stackChoicePanel(panelRef.current, runtime.getDialogBoxes(), runtime.getFrameState().stage.height);
          p.onReveal?.(runtime.getPlayheadPosition());
        }
      },
      onComplete: (event) => {
        removePanel();
        const p = propsRef.current;
        if (event.choiceId) {
          const choice = runtime.getScene().choices.find((c) => c.id === event.choiceId);
          if (choice) p.onChoice?.(choice);
        }
        p.onComplete?.(event);
      },
    });
    runtimeRef.current = runtime;
    const root = rootRef.current;
    if (root) root.smPlayer = handle.current;
    // The conformance hook (spec 017): dev and test builds only
    const unregisterProbe = registerProbe('preview', {
      seek: (ms: number) => handle.current.seek(ms),
      pause: () => handle.current.pause(),
      getFrameState: () => handle.current.getFrameState(),
    });
    fit();
    const { images } = cachedSceneImages(initial as never);
    if (Object.keys(images).length > 0) runtime.setImages(images);
    return () => {
      unregisterProbe();
      playToken.current++;
      runtime.stop();
      runtime.dispose();
      removePanel();
      audio.dispose();
      runtimeRef.current = null;
      audioRef.current = null;
      if (root) delete root.smPlayer;
    };
  }, [apiClient, fit, removePanel, report, revealTime]);

  // Images: decode what the scene needs and hand them over
  const imageKey = sceneImageIds(scene as never).join(',');
  useEffect(() => {
    if (!imageKey) return;
    let cancelled = false;
    void loadSceneImagesForPreview(apiClient, propsRef.current.scene as never).then((images) => {
      if (!cancelled) runtimeRef.current?.setImages(images);
    });
    return () => { cancelled = true; };
  }, [apiClient, imageKey]);

  // Live edits: the scene object changed
  const firstScene = useRef(scene);
  useEffect(() => {
    if (firstScene.current === scene) return;
    runtimeRef.current?.updateScene(scene);
  }, [scene]);

  // Orientation
  useEffect(() => {
    const rt = runtimeRef.current;
    if (!rt) return;
    fit();
    rt.setOrientation(orientation);
    if (panelRef.current) stackChoicePanel(panelRef.current, rt.getDialogBoxes(), rt.getFrameState().stage.height);
  }, [orientation, fit]);

  // Fit the stage into the host whenever the host changes size
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => fit());
    ro.observe(root);
    return () => ro.disconnect();
  }, [fit]);

  // Declarative play: false → true plays from `startMs` (default 0), true → false stops
  useEffect(() => {
    if (!playing) return;
    startPlay(propsRef.current.startMs ?? 0);
    const h = handle.current;
    return () => h.stop();
  }, [playing, startPlay]);

  return (
    <div
      ref={rootRef}
      className={`sm-root${className ? ` ${className}` : ''}`}
      data-player-host=""
      data-testid={props['data-testid'] ?? 'player-host'}
    >
      <style>{PLAYER_STYLE}</style>
      <div ref={stageRef} className="sm-stage" data-story-stage="">
        <canvas ref={canvasRef} className="sm-canvas" aria-hidden="true" data-testid="preview-canvas" />
      </div>
    </div>
  );
});

// ─── The story runner ──────────────────────────────────────────────────────

export interface StoryPlayerHostHandle {
  pause(): void;
  resume(): void;
  /** Play the story again from its start node. */
  restart(): void;
  /** Show the frame at `ms` of the current scene. */
  seek(ms: number): void;
  isPlaying(): boolean;
  getFrameState(): FrameState | null;
}

export interface StoryPlayerHostProps {
  manifest: StoryPackageManifest;
  orientation: Orientation;
  /** Start at this story scene instead of the start node (a pop-out continuing the runner). */
  startAtStorySceneId?: number | null;
  onSceneChange?: (event: StorySceneChangeEvent) => void;
  onSceneComplete?: (storySceneId: number, event: SceneCompleteEvent) => void;
  onPlayhead?: (ms: number) => void;
  className?: string;
  'data-testid'?: string;
}

/** The runtime's own end card is hidden: the editor shows "End of story" or the unconnected notice. */
const RUNNER_STYLE = '[data-story-runner] [data-story-end]{display:none}';

export const StoryPlayerHost = forwardRef<StoryPlayerHostHandle, StoryPlayerHostProps>(function StoryPlayerHost(props, ref) {
  const { manifest, orientation, className } = props;
  const apiClient = useApiClient();
  const containerRef = useRef<HTMLDivElement>(null);
  const runtimeRef = useRef<StoryRuntime | null>(null);
  const propsRef = useRef(props);
  propsRef.current = props;

  useImperativeHandle(ref, () => ({
    pause: () => runtimeRef.current?.pause(),
    resume: () => runtimeRef.current?.resume(),
    restart: () => { void runtimeRef.current?.restart(); },
    seek: (ms: number) => runtimeRef.current?.seek(ms),
    isPlaying: () => runtimeRef.current?.getStatus().playing ?? false,
    getFrameState: () => runtimeRef.current?.getFrameState() ?? null,
  }), []);

  // One runtime per manifest
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const audio = createPreviewAudioPort(apiClient);
    const runtime = new StoryRuntime({
      manifest,
      container,
      orientation: propsRef.current.orientation,
      startOverlay: false,
      audio: audio.port,
      resolveMediaUrl: previewMediaUrl,
      loadImage: (url) => loadPreviewImageUrl(apiClient, url),
      preloadAudio: async () => undefined,
      onSceneChange: (e) => propsRef.current.onSceneChange?.(e),
      onSceneComplete: (id, e) => propsRef.current.onSceneComplete?.(id, e),
      onPlayheadUpdate: (ms) => propsRef.current.onPlayhead?.(ms),
    });
    runtimeRef.current = runtime;
    const startAt = propsRef.current.startAtStorySceneId;
    if (startAt) void runtime.goToScene(startAt);
    else void runtime.play();
    return () => {
      runtime.destroy();
      audio.dispose();
      runtimeRef.current = null;
    };
  }, [apiClient, manifest]);

  useEffect(() => {
    runtimeRef.current?.setOrientation(orientation);
  }, [orientation]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ width: '100%', height: '100%', position: 'relative' }}
      data-story-runner=""
      data-testid={props['data-testid'] ?? 'story-player-host'}
    >
      <style>{RUNNER_STYLE}</style>
    </div>
  );
});

export default PlayerHost;
